import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, TextField, Button } from '@mui/material';
import {
  ArrowBackRounded,
  CurrencyRupeeRounded,
  DateRangeRounded,
} from '@mui/icons-material';
import { colors, brand } from '../../theme/theme';
import { useQueueDashboard, DashboardPeriod } from '../../hooks/useQueueDashboard';
import { PeriodTabs, StatusChips } from './dashboardParts';
import { formatCurrency, sumCounts, formatRange } from './dashboardConfig';

const card = { bgcolor: colors.backgroundPaper, border: `1px solid ${colors.border}`, borderRadius: '14px' };

const RevenuePage: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, error, fetchDashboard } = useQueueDashboard();
  const [period, setPeriod] = useState<DashboardPeriod>('today');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  useEffect(() => {
    if (period !== 'custom') fetchDashboard(period);
  }, [period, fetchDashboard]);

  const total = sumCounts(data?.total_token_counts);

  return (
    <Box>
      <Button startIcon={<ArrowBackRounded />} variant="text" onClick={() => navigate('/dashboard')} sx={{ mb: 2 }}>
        Dashboard
      </Button>

      <Box sx={{ mb: 2 }}><PeriodTabs value={period} onChange={setPeriod} /></Box>
      {period === 'custom' && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <TextField type="date" size="small" value={start} onChange={(e) => setStart(e.target.value)} />
          <Typography sx={{ alignSelf: 'center', color: colors.textSecondary }}>to</Typography>
          <TextField type="date" size="small" value={end} onChange={(e) => setEnd(e.target.value)} />
          <Button variant="contained" disabled={!start || !end} onClick={() => fetchDashboard('custom', start, end)}>Apply</Button>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {isLoading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress /></Box>
      ) : !data ? (
        <Alert severity="info">No revenue data available.</Alert>
      ) : (
        <>
          {/* Earnings hero */}
          <Box sx={{ background: brand.gradBrand, color: '#fff', borderRadius: '14px', p: 2.75, mb: 2.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 48, height: 48, borderRadius: '24px', bgcolor: 'rgba(255,255,255,.16)', display: 'grid', placeItems: 'center' }}>
                <CurrencyRupeeRounded />
              </Box>
              <Box>
                <Typography sx={{ color: 'rgba(255,255,255,.75)', fontWeight: 600 }}>Total Earnings</Typography>
                <Typography sx={{ fontSize: 32, fontWeight: 800, letterSpacing: '-.5px' }}>{formatCurrency(data.total_earnings)}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, color: 'rgba(255,255,255,.8)' }}>
              <DateRangeRounded sx={{ fontSize: 16 }} />
              <Typography sx={{ fontSize: 13 }}>{data.period_label} • {formatRange(data.start_date, data.end_date)}</Typography>
            </Box>
          </Box>

          {/* Token overview */}
          {total > 0 && (
            <Box sx={{ ...card, p: 2.5, mb: 2.75 }}>
              <Typography variant="h5">Token Overview ({total})</Typography>
              <StatusChips counts={data.total_token_counts} />
            </Box>
          )}

          {/* Queue-wise breakdown */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5">Queue-wise Breakdown</Typography>
            <Typography sx={{ color: colors.textSecondary, fontSize: 13 }}>Earnings &amp; token status per queue</Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.25 }}>
            {data.queues.map((q) => {
              const qTotal = sumCounts(q.token_counts);
              return (
                <Box key={q.queue_id} sx={{ ...card, p: 2.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 16 }} noWrap>{q.queue_name}</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 16, color: colors.success, whiteSpace: 'nowrap' }}>{formatCurrency(q.earnings)}</Typography>
                  </Box>
                  {qTotal > 0 ? (
                    <>
                      <Typography sx={{ color: colors.textSecondary, fontSize: 13, mt: 0.5 }}>{qTotal} token{qTotal !== 1 ? 's' : ''}</Typography>
                      <StatusChips counts={q.token_counts} />
                    </>
                  ) : (
                    <Typography sx={{ color: colors.textSecondary, fontSize: 13, mt: 0.5, fontStyle: 'italic' }}>No tokens for this period</Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
};

export default RevenuePage;
